import { useEffect, useMemo, useState } from 'react';
import type { Card, Flow } from '../../types';
import { apiUrl } from '../../utils/apiBase';

// 💰 Painel de verbas e NDs (26/10/2026, pedido do André; dor do Samuel Branco).
// Cada card é uma verba do fornecedor com a sua nota de débito. O painel soma o que foi negociado,
// o que virou ND, o que já entrou e o que falta receber, e afunila: agrupamento (fornecedor,
// comprador ou destino) → as verbas do grupo → o card.
// A aba só aparece em fluxo com os campos "Fornecedor" e "Valor da ND".
// 30/10/2026: compras do Fluxo de compras com "ND vinculada" abatem do saldo (usado em compras / disponível).

interface Props { flow: Flow; cards: Card[]; onAbrirCard: (card: Card) => void; }

const norm = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const todosCampos = (flow: Flow) => (flow.etapas || []).flatMap(e => (e.campos || []).map(c => ({ ...c, etapaId: String(e.id) })));
export const fluxoTemVerbas = (flow: Flow) => {
  const cs = todosCampos(flow);
  return cs.some(c => norm(c.nome) === 'fornecedor') && cs.some(c => /^valor da nd/.test(norm(c.nome)));
};

const numero = (v: unknown) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^\d,.-]/g, '');
  const n = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
  return isNaN(n) ? 0 : n;
};
const texto = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v)).trim();
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dataBR = (d: Date | null) => (d ? d.toLocaleDateString('pt-BR') : '—');

type Fase = 'emitir' | 'cobranca' | 'recebido' | 'cancelada';
type Grupo = 'fornecedor' | 'comprador' | 'destino';
interface Verba { card: Card; fornecedor: string; comprador: string; destino: string; nd: string; etapa: string; fase: Fase;
  negociado: number; valorNd: number; recebido: number; aReceber: number; previsto: Date | null; atrasado: boolean; ano: string;
  usado: number; compras: number; disponivel: number; }

const FASE_ROT: Record<Fase, string> = { emitir: 'A emitir', cobranca: 'Em cobrança', recebido: 'Recebida', cancelada: 'Cancelada' };
const FASE_COR: Record<Fase, string> = { emitir: 'bg-gray-100 text-gray-700', cobranca: 'bg-amber-100 text-amber-800', recebido: 'bg-gray-900 text-white', cancelada: 'bg-red-100 text-red-700' };

export default function VerbasFluxo({ flow, cards, onAbrirCard }: Props) {
  const [grupo, setGrupo] = useState<Grupo>('fornecedor');
  const [ano, setAno] = useState('todos');
  const [aberto, setAberto] = useState<string | null>(null);
  const [soAbertas, setSoAbertas] = useState(false);
  const [usadoMap, setUsadoMap] = useState<Record<string, { usado: number; compras: number }>>({});
  useEffect(() => {
    if (!(flow as any).empresaId && !flow.id) return;
    let vivo = true;
    const p = new URLSearchParams({ verbasFlowId: String(flow.id), empresaId: String((flow as any).empresaId || '') });
    fetch(apiUrl('/verbas-nd/usado') + '?' + p.toString()).then(r => r.json())
      .then(j => { if (vivo && j.success) setUsadoMap(j.usado || {}); }).catch(() => {});
    return () => { vivo = false; };
  }, [flow.id, (flow as any).empresaId, cards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const campo = useMemo(() => {
    const cs = todosCampos(flow);
    const ache = (re: RegExp) => cs.find(c => re.test(norm(c.nome)));
    return { fornecedor: ache(/^fornecedor$/), comprador: ache(/^comprador$/), destino: ache(/^destino da verba/),
      negociado: ache(/^valor negociado/), aprovado: ache(/^valor aprovado/), nd: ache(/^n. da nd|^no da nd|^numero da nd/),
      valorNd: ache(/^valor da nd/), recebido: ache(/^valor recebido/), previsto: ache(/^data prevista de pagamento/),
      emissao: ache(/^data de emissao/) };
  }, [flow]);
  const etapas = flow.etapas || [];
  const ordem = useMemo(() => new Map(etapas.map((e, i) => [String(e.id), i])), [etapas]);
  const nomeEtapa = useMemo(() => new Map(etapas.map(e => [String(e.id), e.nome])), [etapas]);
  const idxEmitir = etapas.findIndex(e => /nd a emitir/.test(norm(e.nome)));

  const verbas = useMemo<Verba[]>(() => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    return cards.map(card => {
      const v = (card.campos || {}) as Record<string, unknown>;
      const val = (c?: { id: string }) => (c ? v[c.id] : undefined);
      const nomeEt = nomeEtapa.get(String(card.etapaId)) || '';
      const i = ordem.get(String(card.etapaId)) ?? 0;
      const n = norm(nomeEt);
      const fase: Fase = /cancel/.test(n) ? 'cancelada' : /finaliz/.test(n) ? 'recebido' : idxEmitir >= 0 && i > idxEmitir ? 'cobranca' : 'emitir';
      const negociado = numero(val(campo.aprovado)) || numero(val(campo.negociado));
      const valorNd = numero(val(campo.valorNd));
      const recebido = fase === 'recebido' ? (numero(val(campo.recebido)) || valorNd || negociado) : 0;
      const aReceber = fase === 'cobranca' ? (valorNd || negociado) : 0;
      const prev = texto(val(campo.previsto));
      const previsto = prev ? new Date(prev) : null;
      const emis = texto(val(campo.emissao)) || String((card as any).createdAt || '');
      const u = usadoMap[String(card.id)];
      const usado = fase === 'cancelada' ? 0 : (u?.usado || 0);
      const aberta = fase === 'emitir' || fase === 'cobranca';
      return { card, fase, negociado, valorNd, recebido, aReceber, previsto, usado, compras: u?.compras || 0,
        disponivel: aberta ? (valorNd || negociado) - usado : 0,
        atrasado: fase === 'cobranca' && !!previsto && !isNaN(+previsto) && previsto < hoje,
        fornecedor: texto(val(campo.fornecedor)).toUpperCase() || '(sem fornecedor)',
        comprador: texto(val(campo.comprador)) || '(sem comprador)', destino: texto(val(campo.destino)) || '(sem destino)',
        nd: texto(val(campo.nd)), etapa: nomeEt, ano: emis.slice(0, 4) };
    });
  }, [cards, campo, nomeEtapa, ordem, idxEmitir, usadoMap]);

  const anos = useMemo(() => [...new Set(verbas.map(v => v.ano).filter(a => /^\d{4}$/.test(a)))].sort().reverse(), [verbas]);
  const base = verbas.filter(v => v.fase !== 'cancelada' && (ano === 'todos' || v.ano === ano));
  const soma = (l: Verba[], k: 'negociado' | 'recebido' | 'aReceber' | 'usado' | 'disponivel') => l.reduce((s, v) => s + v[k], 0);
  const kpis = (l: Verba[]) => ({
    verbas: l.length, negociado: soma(l, 'negociado'), recebido: soma(l, 'recebido'), aReceber: soma(l, 'aReceber'),
    usado: soma(l, 'usado'), disponivel: soma(l, 'disponivel'), compras: l.reduce((s, v) => s + v.compras, 0),
    atrasado: l.filter(v => v.atrasado).reduce((s, v) => s + v.aReceber, 0),
    aEmitir: l.filter(v => v.fase === 'emitir').reduce((s, v) => s + v.negociado, 0) });

  const grupos = useMemo(() => {
    const m = new Map<string, Verba[]>();
    base.forEach(v => { const k = v[grupo]; m.set(k, [...(m.get(k) || []), v]); });
    return [...m.entries()].map(([nome, l]) => ({ nome, l, ...kpis(l) }))
      .sort((a, b) => (b.aReceber - a.aReceber) || (b.negociado - a.negociado));
  }, [base, grupo]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = kpis(base);
  const sel = aberto ? grupos.find(g => g.nome === aberto) : null;
  const k = sel ? kpis(sel.l) : total;
  const maxG = Math.max(1, ...grupos.map(g => g.negociado));
  const lista = (sel ? sel.l : []).filter(v => !soAbertas || v.fase === 'emitir' || v.fase === 'cobranca')
    .sort((a, b) => (Number(b.atrasado) - Number(a.atrasado)) || (b.aReceber - a.aReceber) || (+(b.previsto || 0) - +(a.previsto || 0)));
  const ROT_G: Record<Grupo, string> = { fornecedor: 'Fornecedor', comprador: 'Comprador', destino: 'Destino da verba' };

  const Kpi = ({ t, v, s, cor }: { t: string; v: string; s?: string; cor?: string }) => (
    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
      <div className={`text-2xl font-extrabold tracking-tight ${cor || 'text-gray-900'}`}>{v}</div>
      <div className="text-xs text-gray-500 mt-0.5">{t}</div>{s && <div className="text-[11px] text-gray-400 mt-1">{s}</div>}
    </div>);

  return (
    <div className="p-4 md:p-6 space-y-4 bg-gray-50 min-h-full">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm">
          <button onClick={() => setAberto(null)} className={`px-3 py-1 rounded-full border ${sel ? 'bg-white text-gray-700 border-gray-200 hover:border-gray-400' : 'bg-gray-900 text-white border-gray-900'}`}>
            Todas as verbas</button>
          {sel && <><span className="text-gray-400">›</span><span className="px-3 py-1 rounded-full bg-gray-900 text-white font-semibold">{sel.nome}</span></>}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">Agrupar por</span>
          {(['fornecedor', 'comprador', 'destino'] as Grupo[]).map(g => (
            <button key={g} onClick={() => { setGrupo(g); setAberto(null); }}
              className={`px-2.5 py-1 rounded-full border ${grupo === g ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-600'}`}>{ROT_G[g]}</button>))}
          <select value={ano} onChange={e => { setAno(e.target.value); setAberto(null); }} className="border border-gray-200 rounded-lg px-2 py-1 bg-white">
            <option value="todos">Todos os anos</option>{anos.map(a => <option key={a} value={a}>{a}</option>)}</select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi t="Verba negociada" v={brl(k.negociado)} s={`${k.verbas} verbas`} />
        <Kpi t="Já recebido" v={brl(k.recebido)} s="depósito ou abatimento" />
        <Kpi t="A receber (ND emitida)" v={brl(k.aReceber)} cor="text-amber-700" />
        <Kpi t="Atrasado" v={brl(k.atrasado)} s="previsão de pagamento vencida" cor={k.atrasado ? 'text-red-600' : 'text-gray-900'} />
        <Kpi t="Verba ainda sem ND" v={brl(k.aEmitir)} s="negociada, falta emitir" />
        <Kpi t="Usado em compras" v={brl(k.usado)} s={`${k.compras} compra${k.compras === 1 ? '' : 's'} com ND vinculada`} />
        <Kpi t="Saldo disponível" v={brl(k.disponivel)} s="NDs em aberto menos o usado" cor={k.disponivel < 0 ? 'text-red-600' : 'text-emerald-700'} />
      </div>

      {!sel && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">Por {ROT_G[grupo].toLowerCase()} · clique para detalhar</div>
          <div className="divide-y divide-gray-50">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400">
              <span className="col-span-4">{ROT_G[grupo]}</span><span className="col-span-1 text-right">Verbas</span><span className="col-span-2 text-right">Negociado</span>
              <span className="col-span-2 text-right">Recebido</span><span className="col-span-2 text-right">A receber</span><span className="col-span-1 text-right">Atrasado</span></div>
            {grupos.map(g => (
              <button key={g.nome} onClick={() => setAberto(g.nome)} className="w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center hover:bg-gray-50 text-left">
                <span className="col-span-4"><b className="font-semibold text-gray-900">{g.nome}</b>
                  <span className="block h-1.5 mt-1 bg-gray-100 rounded overflow-hidden"><i className="block h-full bg-gray-900" style={{ width: `${g.negociado / maxG * 100}%` }} /></span>
                  {g.usado > 0 && <span className="block text-[11px] text-gray-500 mt-0.5">usado em compras {brl(g.usado)} · disponível {brl(g.disponivel)}</span>}</span>
                <span className="col-span-1 text-right text-gray-600">{g.verbas}</span>
                <span className="col-span-2 text-right">{brl(g.negociado)}</span><span className="col-span-2 text-right text-gray-600">{brl(g.recebido)}</span>
                <span className={`col-span-2 text-right font-semibold ${g.aReceber ? 'text-amber-700' : 'text-gray-400'}`}>{brl(g.aReceber)}</span>
                <span className={`col-span-1 text-right ${g.atrasado ? 'text-red-600 font-semibold' : 'text-gray-300'}`}>{g.atrasado ? brl(g.atrasado) : '—'}</span>
              </button>))}
            {!grupos.length && <div className="px-4 py-8 text-center text-sm text-gray-400">Nenhuma verba neste filtro.</div>}
          </div>
        </div>)}

      {sel && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 text-sm">
            <span className="font-semibold text-gray-900">Verbas de {sel.nome}</span>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-gray-500"><input type="checkbox" checked={soAbertas} onChange={e => setSoAbertas(e.target.checked)} /> só as em aberto</label>
          </div>
          <div className="divide-y divide-gray-50">
            {lista.map(v => (
              <button key={String(v.card.id)} onClick={() => onAbrirCard(v.card)} className={`w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center text-left hover:bg-gray-50 ${v.atrasado ? 'bg-red-50/50' : ''}`}>
                <span className="col-span-5"><b className="font-medium text-gray-900">{v.card.titulo}</b>
                  <span className="block text-[11px] text-gray-500">{[v.nd && `ND ${v.nd}`, grupo !== 'comprador' && v.comprador, grupo !== 'destino' && v.destino, grupo !== 'fornecedor' && v.fornecedor].filter(Boolean).join(' · ')}</span>
                  {v.usado > 0 && <span className="block text-[11px] text-emerald-700">🧾 usado em {v.compras} compra{v.compras === 1 ? '' : 's'}: {brl(v.usado)}{(v.fase === 'emitir' || v.fase === 'cobranca') ? ` · disponível ${brl(v.disponivel)}` : ''}</span>}</span>
                <span className="col-span-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${FASE_COR[v.fase]}`}>{FASE_ROT[v.fase]}</span>
                  <span className="block text-[11px] text-gray-400 mt-0.5">{v.etapa}</span></span>
                <span className="col-span-2 text-right">{brl(v.valorNd || v.negociado)}</span>
                <span className={`col-span-2 text-right text-xs ${v.atrasado ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{v.fase === 'cobranca' ? `prev. ${dataBR(v.previsto)}` : ''}</span>
                <span className="col-span-1 text-right text-gray-300">›</span>
              </button>))}
            {!lista.length && <div className="px-4 py-8 text-center text-sm text-gray-400">Nada em aberto.</div>}
          </div>
        </div>)}
    </div>
  );
}
